import type { TFunction } from 'i18next';
import type { Initiative, KPIOverviewData } from '../../../../api/initiative-client/initiative-client.type';
import {
  graphLightRed,
  graphPictonBlue,
  graphPistachio,
  graphSunglow,
  skyBase,
} from '../../../../styles/design-tokens';
import type { InitiativeByStatusDataPoint } from './overview.type';

/**
 * Transforms initiative status data into a format suitable for visualization
 *
 * @param data - KPI overview data containing initiative status information
 * @param t - Translation function from `useTranslation()`
 * @returns Array of data points containing category, value and fill color for each status
 */
const getStatusesData = (data: KPIOverviewData | null, t: TFunction): InitiativeByStatusDataPoint[] => {
  if (!data || !data.initiatives_by_status || !data.initiatives_by_status.by_tasks) {
    return [];
  }

  const { by_tasks } = data.initiatives_by_status;

  return [
    {
      category: t('strategy.initiatives.statuses.off_track'),
      value: by_tasks.off_track.value,
      fill: graphLightRed,
    },
    { category: t('strategy.initiatives.statuses.at_risk'), value: by_tasks.at_risk.value, fill: graphSunglow },
    {
      category: t('strategy.initiatives.statuses.on_track'),
      value: by_tasks.on_track.value,
      fill: graphPistachio,
    },
  ];
};

/**
 * Transforms initiative status data into a format suitable for visualization, focusing on empty and unknown statuses
 *
 * @param initiatives - Array of initiatives to analyze for empty status
 * @param t - Translation function from `useTranslation()`
 * @returns Array of data points containing category, value and fill color for empty and unknown date statuses
 */
const getStatusesExtraData = (initiatives: Initiative[], t: TFunction): InitiativeByStatusDataPoint[] => {
  if (initiatives.length === 0) {
    return [];
  }

  const noEpicsInitiatives = initiatives.filter((initiative) => initiative.epic_count === 0);
  const noEndDateInitiatives = initiatives.filter((initiative) => initiative.no_end_date);

  return [
    {
      category: t('strategy.initiatives.no_epics'),
      value: noEpicsInitiatives.length,
      fill: skyBase,
    },
    {
      category: t('strategy.initiatives.no_end_date'),
      value: noEndDateInitiatives.length,
      fill: graphPictonBlue,
    },
  ];
};

export { getStatusesData, getStatusesExtraData };
